import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';

import {
    HERO_MODULE_TYPE,
    PHOTO_CAROUSE_MODULE_TYPE,
    CREATOR_SPOTLIGHT_TYPE,
    MAIN_CONTENT_MODULE_TYPE,
} from '../../constants/collectionsModulesConstatns';

import { SbSharedCollectionsModulesHeroModule } from '../SbSharedCollectionsModulesHeroModule/SbSharedCollectionsModulesHeroModule';
import { SbRespCollectionsComponents } from '../SbRespCollectionsComponents/SbRespCollectionsComponents';
import { SharedProductCarousel } from '../../shared/SharedProductCarousel/SharedProductCarousel';
import { SbRespCreatorSpotlightModule } from '../SbRespCreatorSpotlightModule/SbRespCreatorSpotlightModule';

const SbRespCollectionsModulesComponent = ({
    searchBrowse,
    viewer,
    user,
    currency,
    isClient,
    shippingFilters,
}) => {
    const { collectionsModules } = searchBrowse;
    const { isCollectionsModuleEnabled } = viewer;
    const mainContentModule = (
        <Fragment key={MAIN_CONTENT_MODULE_TYPE}>
            <SbRespCollectionsComponents
                viewer={viewer}
                searchBrowse={searchBrowse}
                user={user}
                currency={currency}
                isClient={isClient}
                shippingFilters={shippingFilters}
                collectionsModules={null}
            />
        </Fragment>
    );

    if (!collectionsModules || !isCollectionsModuleEnabled) {
        return mainContentModule;
    }

    let isSetMainContentModule = false;

    const modules = collectionsModules.map(collectionsModule => {
        switch (collectionsModule.moduleType) {
            case HERO_MODULE_TYPE:
                return (
                    <SbSharedCollectionsModulesHeroModule
                        key={collectionsModule.id}
                        collectionsModules={collectionsModule}
                        renderImage={({ src, imgClassName, alt }) => (
                            <img src={src} className={imgClassName} alt={alt} />
                        )}
                    />
                );
            case PHOTO_CAROUSE_MODULE_TYPE:
                return (
                    <SharedProductCarousel
                        key={collectionsModule.id}
                        collectionsModules={collectionsModule}
                        showOverlay
                        isClient={isClient}
                    />
                );
            case MAIN_CONTENT_MODULE_TYPE:
                isSetMainContentModule = true;
                return (
                    <SbRespCollectionsComponents
                        key={collectionsModule.id}
                        viewer={viewer}
                        searchBrowse={searchBrowse}
                        user={user}
                        currency={currency}
                        isClient={isClient}
                        shippingFilters={shippingFilters}
                        // passing collectionsModules only here since this is the only case where we don't need to use default data
                        collectionsModules={collectionsModule}
                    />
                );
            case CREATOR_SPOTLIGHT_TYPE:
                return (
                    <SbRespCreatorSpotlightModule
                        key={collectionsModule.id}
                        collectionsModules={collectionsModule}
                        isClient={isClient}
                    />
                );
            default:
                return null;
        }
    });

    if (!isSetMainContentModule) {
        modules.push(mainContentModule);
    }

    return modules;
};

SbRespCollectionsModulesComponent.propTypes = {
    searchBrowse: PropTypes.object.isRequired,
    user: PropTypes.object,
    viewer: PropTypes.object,
    currency: PropTypes.string.isRequired,
    isClient: PropTypes.bool,
    shippingFilters: PropTypes.array,
};

export const SbRespCollectionsModules = createFragmentContainer(SbRespCollectionsModulesComponent, {
    searchBrowse: graphql`
        fragment SbRespCollectionsModules_searchBrowse on SearchBrowseQueryConnection {
            ...SbRespCollectionsComponents_searchBrowse
            collectionsModules(previewId: $contentModuleId) {
                moduleType
                id
                ...SbSharedCollectionsModulesHeroModule_collectionsModules
                ...SharedProductCarousel_collectionsModules
                ...SbRespCollectionsComponents_collectionsModules
                ...SbRespCreatorSpotlightModule_collectionsModules
            }
        }
    `,
    viewer: graphql`
        fragment SbRespCollectionsModules_viewer on Viewer {
            isCollectionsModuleEnabled: isEnabledByLocale(featureName: "BROWSE_COLLECTIONS_MODULES")
            ...SbRespCollectionsComponents_viewer
        }
    `,
    user: graphql`
        fragment SbRespCollectionsModules_user on User {
            ...SbRespCollectionsComponents_user
        }
    `,
    shippingFilters: graphql`
        fragment SbRespCollectionsModules_shippingFilters on ShippingFilterType
            @relay(plural: true) {
            ...SbRespCollectionsComponents_shippingFilters
        }
    `,
});
